import { createActionGroup, props } from '@ngrx/store';
import { JobDetailsV2Fragment } from 'src/generated/graphql.generated';

import { InventoryChanges, KeptInventoryItem, NavigatorSaveDropped, NavigatorWrite, QueuedNavigatorWrite } from './navigator-outbox.util';

/**
 * The crew's navigator writes that are waiting for signal (#86a).
 *
 * Its own slice, deliberately not the shared unsaved-changes queue: that queue's readers would show
 * these saves on the job page's banner, delete them on Discard, purge them on a calculator error
 * and persist them only for pinned or recent jobs.
 */
export const NavigatorOutboxActions = createActionGroup({
  source: 'Navigator Outbox',
  events: {
    /** A write failed for lack of signal, or was built while earlier ones were still waiting. */
    'Save Queued': props<{ entry: QueuedNavigatorWrite }>(),
    /**
     * A write went straight through. Queued writes built before it lose the keys it carried, so a
     * replay cannot put back what it replaced — see `withoutKeysWrittenBy`.
     */
    'Direct Save Stored': props<{ seq: number; write: NavigatorWrite }>(),
    'Replay Save Success': props<{ id: string }>(),
    /** The server refused a waiting write. It leaves the outbox: resending would be refused again. */
    'Replay Save Refused': props<{ id: string; error: Error }>(),
    /** A reply carried the event's ops revision; later writes are based on it (#86b). */
    'Ops Revision Seen': props<{ eventId: string; opsRevision: number }>(),
    /**
     * The server did not apply part or all of a crew write: ops had moved the crew (`moved`) or reset
     * the run (`reset`) since it was built (#86b). Carries the run as stored, so the crew can be put
     * where ops left them.
     */
    'Save Dropped': props<{
      jobId: string | null;
      eventId: string;
      reason: NavigatorSaveDropped;
      currentStep: string | null;
      navigatorData: string | null;
    }>(),
    /**
     * This device's copy of the job now holds a new inventory list (#86c): the crew's own at Confirm
     * Scope, so a reload while the save waits still shows their edits, and the server's merged list
     * when the save lands. Loads the inventory editor from it, as a job load does.
     *
     * `changeIds`: the crew's inventory edits the outbox now owns, which leave the job page's
     * unsaved changes so a later job-page save cannot send the whole list again.
     */
    'Job Inventory Updated Locally': props<{ jobId: string; job: JobDetailsV2Fragment; changeIds: string[] }>(),
    /**
     * A save with the crew's inventory changes landed (#86c). `inventory` is the job's list as stored
     * after the merge; `kept`, the crew's items where the office's version was kept.
     */
    'Inventory Merged': props<{
      jobId: string | null;
      eventId: string;
      inventory: string | null;
      kept: KeptInventoryItem[];
    }>(),
    /**
     * The server refused a save with the crew's inventory changes — sent at once or after waiting
     * (an expired login, a server error). This device's copy already holds them as stored, so they
     * are put back as the crew's unsaved edits, for the next Confirm Scope to send again.
     */
    'Inventory Save Refused': props<{ jobId: string | null; eventId: string; changes: InventoryChanges }>(),
  },
});
